import { useMemo, useCallback } from 'react';
import { useUnitConversion } from './useUnitConversion';
import { UnitsEnum } from '@/__generated__/graphql';

// Generic calculation data interface
export interface CalculationData {
    totalItems: number;
    selectedItems: number;
    primaryValue: number;
    totalValue: number;
    percentage: number;
    hasData: boolean;
}

// Generic calculation options
export interface CalculationOptions<T = unknown, ItemType = unknown> {
    data?: T;
    selectedItems: string[];
    getItemName: (item: ItemType) => string;
    getPrimaryValue: (item: ItemType) => number;
    getTotalValue: (item: ItemType) => number;
    getTotalCount: (data: T) => number;
    getItems: (data: T) => ItemType[];
    unit?: UnitsEnum;
}

// Generic calculation hook
export const useCalculationData = <T = unknown, ItemType = unknown>({
    data,
    selectedItems,
    getItemName,
    getPrimaryValue,
    getTotalValue,
    getTotalCount,
    getItems,
    unit,
}: CalculationOptions<T, ItemType>): CalculationData => {
    const { getUnitSuffix } = useUnitConversion();

    const convertValue = useCallback((value: number): number => {
        if (!unit) return value;
        const unitSuffix = getUnitSuffix({ unit });
        if (unit === UnitsEnum.SquareMeter) {
            if (unitSuffix === 'ft²') {
                return value * 10.7639;
            }
            return value;
        }
        return value;
    }, [unit, getUnitSuffix]);
    return useMemo(() => {
        const hasValidData = data && Object.keys(data).length > 0;

        if (!hasValidData) {
            console.log('useCalculationData: No valid data provided', { data });
            return {
                totalItems: 0,
                selectedItems: selectedItems.length,
                primaryValue: 0,
                totalValue: 0,
                percentage: 0,
                hasData: false,
            };
        }

        try {
            const allItems = getItems(data);
            const selectedItemNames = new Set(selectedItems);

            const selectedItemsData = allItems.filter(item => {
                const itemName = getItemName(item);
                const isSelected = selectedItemNames.has(itemName);
                return isSelected;
            });

            const { primaryValue, totalValue } = selectedItemsData.reduce<{
                primaryValue: number;
                totalValue: number
            }>(
                (acc, item) => {
                    const itemPrimaryValue = getPrimaryValue(item) || 0;
                    const itemTotalValue = getTotalValue(item) || 0;

                    return {
                        primaryValue: acc.primaryValue + itemPrimaryValue,
                        totalValue: acc.totalValue + itemTotalValue,
                    };
                },
                { primaryValue: 0, totalValue: 0 }
            );

            const convertedPrimaryValue = convertValue(primaryValue);
            const convertedTotalValue = convertValue(totalValue);

            const percentage = convertedTotalValue > 0
                ? Math.round((convertedPrimaryValue / convertedTotalValue) * 100)
                : 0;

            const totalCount = typeof getTotalCount === 'function' ? getTotalCount(data) : 0;

            const roundToTwo = (num: number): number => Math.round((num + Number.EPSILON) * 100) / 100;

            const result = {
                totalItems: totalCount,
                selectedItems: selectedItemsData.length,
                primaryValue: roundToTwo(convertedPrimaryValue),
                totalValue: roundToTwo(convertedTotalValue),
                percentage,
                hasData: true,
            };

            return result;
        } catch (error) {
            console.error('Error in useCalculationData:', error);
            return {
                totalItems: 0,
                selectedItems: 0,
                primaryValue: 0,
                totalValue: 0,
                percentage: 0,
                hasData: false,
            };
        }
    }, [data, selectedItems, getItemName, getPrimaryValue, getTotalValue, getTotalCount, getItems, convertValue]);
};